import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, TrendingUp, Users, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Finance({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('active');
  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      const [statsRes, subsRes] = await Promise.all([
        axios.get(`${API}/coach/finance/stats`),
        axios.get(`${API}/coach/finance/subscriptions`)
      ]);

      setStats(statsRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/coach/finance/sync`);
      toast.success('Finance data synced with Stripe');
      await fetchFinanceData();
    } catch (error) {
      toast.error('Failed to sync with Stripe');
    } finally {
      setSyncing(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    // Filter by search term
    const matchesSearch = searchTerm ? 
      `${sub.client_name} ${sub.client_email}`.toLowerCase().includes(searchTerm.toLowerCase()) :
      true;
    
    // Filter by active tab
    const matchesTab = sub.category === activeSubTab;
    
    return matchesSearch && matchesTab;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div data-testid="loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a61e8]/5 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="finance-title">
              Finance Dashboard
            </h1>
            <p className="text-lg text-gray-600">Revenue and subscription metrics</p>
          </div>
          <Button onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sync with Stripe
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">MRR</p>
                  <p className="text-3xl font-bold text-[#1a61e8]" data-testid="mrr">
                    ${stats?.mrr?.toLocaleString() || 0}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-[#1a61e8]" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">ARR</p>
                  <p className="text-3xl font-bold text-blue-600" data-testid="arr">
                    ${stats?.arr?.toLocaleString() || 0}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Subs</p>
                  <p className="text-3xl font-bold" data-testid="active-subs">
                    {stats?.active_subscriptions || 0}
                  </p>
                </div>
                <Users className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Failed Payments</p>
                  <p className="text-3xl font-bold text-red-600" data-testid="failed-payments">
                    {stats?.failed_payments || 0}
                  </p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="subscriptions" className="space-y-6">
          <TabsList>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="charts">Revenue Charts</TabsTrigger>
          </TabsList>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <CardTitle>Client Subscriptions</CardTitle>
                <CardDescription>View and manage all client subscriptions by status</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs for Active/Inactive/Cancelled */}
                <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
                  <TabsList className="mb-4">
                    <TabsTrigger value="active" className="flex items-center gap-2">
                      Active
                      <Badge variant="secondary" className="ml-1">
                        {subscriptions.filter(s => s.category === 'active').length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="inactive" className="flex items-center gap-2">
                      Inactive
                      <Badge variant="secondary" className="ml-1">
                        {subscriptions.filter(s => s.category === 'inactive').length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="cancelled" className="flex items-center gap-2">
                      Cancelled
                      <Badge variant="secondary" className="ml-1">
                        {subscriptions.filter(s => s.category === 'cancelled').length}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>

                  <div className="mb-4">
                    <Input
                      type="text"
                      placeholder="Search by client name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">Client</th>
                          <th className="text-left py-3 px-4">Plan</th>
                          <th className="text-left py-3 px-4">Status</th>
                          <th className="text-right py-3 px-4">Amount</th>
                          <th className="text-left py-3 px-4">Start Date</th>
                          <th className="text-left py-3 px-4">End Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubscriptions.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-8 text-gray-500">
                              No {activeSubTab} subscriptions found
                            </td>
                          </tr>
                        ) : (
                          filteredSubscriptions.map((sub) => (
                            <tr key={sub.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div>
                                    <p className="font-semibold">{sub.client_name}</p>
                                    <p className="text-sm text-gray-600">{sub.client_email}</p>
                                  </div>
                                  {sub.is_free && (
                                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                      FREE
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4 capitalize">{sub.plan}</td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded text-sm ${
                                  sub.status === 'active' && !sub.cancel_at_period_end ? 'bg-green-100 text-green-700' :
                                  sub.cancel_at_period_end ? 'bg-orange-100 text-orange-700' :
                                  sub.status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {sub.cancel_at_period_end ? 'Cancels ' + formatDate(sub.ends_at) : sub.status}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right font-semibold">
                                {sub.is_free ? (
                                  <span className="text-green-600">£0</span>
                                ) : (
                                  `£${(sub.amount / 100).toFixed(2)}`
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {formatDate(sub.started_at)}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {sub.category === 'active' && !sub.cancel_at_period_end ? (
                                  <span className="text-gray-400">N/A</span>
                                ) : (
                                  formatDate(sub.ends_at)
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>MRR Trend</CardTitle>
                <CardDescription>Monthly recurring revenue over time</CardDescription>
              </CardHeader>
              <CardContent>
                {stats?.mrr_chart ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.mrr_chart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="mrr" stroke="#16a34a" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center py-8 text-gray-500">No chart data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>New vs. Canceled</CardTitle>
                <CardDescription>Monthly subscriptions activity</CardDescription>
              </CardHeader>
              <CardContent>
                {stats?.activity_chart ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.activity_chart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="new" fill="#16a34a" />
                      <Bar dataKey="canceled" fill="#dc2626" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center py-8 text-gray-500">No chart data available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
